"use client"

import { useEffect, useState } from "react"
import { FIELD_UNDERLINE, FieldShell } from "@/components/graphite/field"
import { PageAction } from "@/components/graphite/page-header"
import { QuietAction } from "@/components/graphite/surface"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { cn } from "@/lib/utils"
import { getGetCompaniesQueryKey, usePostCompanies } from "openapi/api/endpoints"
import type { Company } from "openapi/api/model"
import { toast } from "sonner"
import { useQueryClient } from "@tanstack/react-query"
import { INDUSTRIES } from "@/lib/industries"

type CompanyFormData = {
  name: string
  industry: string
  size: string
  location: string
  website: string
  description: string
}

interface CreateCompanyFormProps {
  initialName?: string
  submitLabel?: string
  cancelLabel?: string
  onCancel?: () => void
  onCreated?: (company: Company) => void
}

const EMPTY_FORM: CompanyFormData = {
  name: "",
  industry: "",
  size: "",
  location: "",
  website: "",
  description: "",
}

export function CreateCompanyForm({
  initialName = "",
  submitLabel = "Create Company",
  cancelLabel = "Cancel",
  onCancel,
  onCreated,
}: CreateCompanyFormProps) {
  const [formData, setFormData] = useState<CompanyFormData>({ ...EMPTY_FORM, name: initialName })
  const { mutateAsync: createCompany, isPending } = usePostCompanies()
  const queryClient = useQueryClient()

  useEffect(() => {
    setFormData((prev) => ({ ...prev, name: initialName }))
  }, [initialName])

  const handleSubmit = async () => {
    if (!formData.name.trim()) return

    try {
      const createdCompany = await createCompany({
        data: {
          name: formData.name.trim(),
          industry: formData.industry.trim() || "",
          size: formData.size || "",
          location: formData.location.trim() || "",
          website: formData.website.trim() || "",
          description: formData.description.trim() || "",
        },
      })

      await queryClient.invalidateQueries({ queryKey: getGetCompaniesQueryKey() })
      toast.success("Company created successfully.")
      onCreated?.(createdCompany)
      setFormData(EMPTY_FORM)
    } catch (err) {
      toast.error("Failed to create company.", {
        description: `${err}`,
      })
    }
  }

  const CONTROL = cn(FIELD_UNDERLINE, "pb-[7px] text-[12.5px] leading-[1.4]")
  const TRIGGER = cn(CONTROL, "h-auto")

  return (
    <div>
      <FieldShell
        label={
          <>
            Company name <span className="text-destructive">*</span>
          </>
        }
      >
        <input
          id="create-company-name"
          value={formData.name}
          onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
          placeholder="Enter company name"
          aria-label="Company name"
          className={CONTROL}
        />
      </FieldShell>

      {/* `@sm`, not a fixed two columns: this form is rendered inside a dialog
          and also inside the community rail, which is far narrower. */}
      <div className="mt-5 grid grid-cols-1 gap-5 @sm:grid-cols-2">
        <FieldShell label="Industry">
          <Select
            value={formData.industry || "__none__"}
            onValueChange={(value) =>
              setFormData((prev) => ({ ...prev, industry: value === "__none__" ? "" : value }))
            }
          >
            <SelectTrigger id="create-company-industry" aria-label="Industry" className={TRIGGER}>
              <SelectValue placeholder="Select industry" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">No industry</SelectItem>
              {INDUSTRIES.map((industry) => (
                <SelectItem key={industry} value={industry}>
                  {industry}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FieldShell>

        <FieldShell label="Company size">
          <Select
            value={formData.size}
            onValueChange={(value) => setFormData((prev) => ({ ...prev, size: value }))}
          >
            <SelectTrigger id="create-company-size" aria-label="Company size" className={TRIGGER}>
              <SelectValue placeholder="Select size" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1-10">1-10 employees</SelectItem>
              <SelectItem value="11-50">11-50 employees</SelectItem>
              <SelectItem value="51-200">51-200 employees</SelectItem>
              <SelectItem value="201-500">201-500 employees</SelectItem>
              <SelectItem value="501-1000">501-1000 employees</SelectItem>
              <SelectItem value="1000+">1000+ employees</SelectItem>
            </SelectContent>
          </Select>
        </FieldShell>
      </div>

      <div className="mt-5 grid grid-cols-1 gap-5 @sm:grid-cols-2">
        <FieldShell label="Location">
          <input
            id="create-company-location"
            value={formData.location}
            onChange={(e) => setFormData((prev) => ({ ...prev, location: e.target.value }))}
            placeholder="e.g., San Francisco, CA"
            aria-label="Location"
            className={CONTROL}
          />
        </FieldShell>
        <FieldShell label="Website">
          <input
            id="create-company-website"
            value={formData.website}
            onChange={(e) => setFormData((prev) => ({ ...prev, website: e.target.value }))}
            placeholder="https://company.com"
            aria-label="Website"
            className={CONTROL}
          />
        </FieldShell>
      </div>

      <FieldShell label="Description" className="mt-5">
        <textarea
          id="create-company-description"
          value={formData.description}
          onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
          placeholder="Brief description of the company..."
          rows={3}
          aria-label="Description"
          className={cn(FIELD_UNDERLINE, "resize-none pb-2.5 text-[12.5px] leading-[1.7]")}
        />
      </FieldShell>

      <div className="mt-6 flex items-center justify-end gap-2">
        <QuietAction size="md" onClick={onCancel} disabled={isPending}>
          {cancelLabel}
        </QuietAction>
        <PageAction
          tone="primary"
          onClick={handleSubmit}
          disabled={isPending || !formData.name.trim()}
        >
          {isPending ? "Creating…" : submitLabel}
        </PageAction>
      </div>
    </div>
  )
}
